import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useGroupRoutines } from '@gripp/shared-logic';
import {
  Colors,
  EntityAlertListModal,
  FilterType,
  GroupAlertModal,
  GroupSearchInput,
  GroupsList,
} from '@gripp/shared-ui';
import React, { useState } from 'react';
import { Pressable, StyleProp, StyleSheet, Text, View } from 'react-native';
import { FilterBar } from '../../shared/components/filter/filterBar';
import { ModalButton } from '../../shared/components/modalButton';
import { useMaxHeight } from '../../shared/hooks/useMaxHeight';
import { EditGroupMenu } from './editGroupMenu';
import { GroupAlertButton } from './groupAlertButton';
import { AssignmentType, GroupAssignmentForm } from './groupAssignmentForm';

export const Groups = () => {
  const { containerMaxHeight, listMaxHeight } = useMaxHeight(64, 0, 228);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showAlertListModal, setShowAlertListModal] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | undefined>(
    undefined
  );
  const { routinesCount } = useGroupRoutines(selectedGroupId);

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const AssetsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assets)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.Assets}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const PeopleButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="account-multiple"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.people)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.People}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const RoutinesButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="format-list-checks"
              color={Colors.blue}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.routines)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.Routines}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const GroupAlertsButton = (data: any) => {
    return (
      <Pressable
        onPress={() => {
          setSelectedGroupId(data.id);
          setShowAlertListModal(true);
        }}
        style={[styles.iconContainer]}
      >
        <MaterialCommunityIcons
          name="alarm-check"
          color={Colors.blackText}
          size={16}
          style={styles.icon}
        />
        {getLabel(styles.countText, data.groupAlerts)}
      </Pressable>
    );
  };

  //

  return (
    <>
      <View style={styles.filtersContainer}>
        <View style={styles.groupSearchContainer}>
          <GroupSearchInput />
        </View>
        <FilterBar filterButtonTypes={[FilterType.GROUPCOLUMNS]} />
      </View>
      <View
        style={[
          styles.groupsContainer,
          {
            maxHeight: containerMaxHeight,
          },
        ]}
      >
        <GroupsList
          maxHeight={listMaxHeight}
          assetButton={AssetsButton}
          peopleButton={PeopleButton}
          routinesButton={RoutinesButton}
          groupAlertsButton={GroupAlertsButton}
          editGroupMenu={EditGroupMenu}
          createAlertButton={(groupId?: string) => {
            if (!groupId) return null;
            setSelectedGroupId(groupId);
            return (
              <GroupAlertButton
                onPress={() => setShowAlertModal(true)}
                disabled={routinesCount === 0}
              />
            );
          }}
        />
        {showAlertModal && selectedGroupId && (
          <GroupAlertModal
            visible={showAlertModal}
            groupId={selectedGroupId}
            onClose={() => setShowAlertModal(false)}
          />
        )}
        {showAlertListModal && selectedGroupId && (
          <EntityAlertListModal
            visible={showAlertListModal}
            entityType="group"
            entityId={selectedGroupId}
            onClose={() => {
              setShowAlertListModal(false);
              setSelectedGroupId(undefined);
            }}
          />
        )}
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 44,
    marginBottom: 20,
  },
  groupSearchContainer: {
    width: 390,
    marginRight: 20,
  },
  groupsContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  headerButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
